import { Directive, ElementRef, Input, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { LoadingService } from '../common/LoadingService';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Directive to show loading spinner on buttons
 *
 * Usage:
 * <button [loadingButton]="'unique-key'" (click)="doSomething()">
 *   Submit
 * </button>
 *
 * Then in component:
 * this.loadingService.showButtonSpinner('unique-key');
 * // ... async operation ...
 * this.loadingService.hideButtonSpinner('unique-key');
 */
@Directive({
  selector: '[loadingButton]',
  standalone: false
})
export class LoadingButtonDirective implements OnInit, OnDestroy {
  @Input('loadingButton') buttonKey: string;

  private subscription: Subscription;
  private originalContent: string = '';
  private spinnerElement: HTMLElement;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private loadingService: LoadingService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.buttonKey) {
      console.warn('LoadingButtonDirective: buttonKey is required');
      return;
    }

    // Store original button content
    this.originalContent = this.el.nativeElement.innerHTML;

    // Create spinner element
    this.spinnerElement = this.renderer.createElement('span');
    this.renderer.addClass(this.spinnerElement, 'spinner-border');
    this.renderer.addClass(this.spinnerElement, 'spinner-border-sm');
    this.renderer.setStyle(this.spinnerElement, 'margin-right', '5px');
    this.renderer.setAttribute(this.spinnerElement, 'role', 'status');
    this.renderer.setAttribute(this.spinnerElement, 'aria-hidden', 'true');

    // Subscribe to loading state
    this.subscription = this.loadingService.isButtonLoading$(this.buttonKey).subscribe(
      (isLoading: boolean) => {
        if (isLoading) {
          this.showSpinner();
        } else {
          this.hideSpinner();
        }
      }
    );
  }

  private showSpinner(): void {
    // Disable button
    this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');

    // Clear current content and add spinner
    this.el.nativeElement.innerHTML = '';
    this.renderer.appendChild(this.el.nativeElement, this.spinnerElement.cloneNode(true));

    // Add localized loading text
    const loadingText = this.renderer.createText(' ' + this.translate.instant('Loading'));
    this.renderer.appendChild(this.el.nativeElement, loadingText);
  }

  private hideSpinner(): void {
    // Enable button
    this.renderer.removeAttribute(this.el.nativeElement, 'disabled');

    // Restore original content
    this.el.nativeElement.innerHTML = this.originalContent;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
